
export const planImageDemo = '/img/plan/conference-speaker.jpg'

/**
 * External help site embedded on the manual page. Owned and maintained by
 * LaConcept, so its content changes without an app deployment. Override per
 * environment with `VITE_HELP_URL`.
 */
export const HELP_SITE_URL: string =
  import.meta.env.VITE_HELP_URL || 'https://help.la-concept.de/'

/**
 * Whether the help site may be shown in an iframe. A browser gives no
 * JS-observable signal when it refuses a frame (`X-Frame-Options` /
 * `frame-ancestors`), so this cannot be feature-detected — it has to be
 * configured. Set `VITE_HELP_EMBED=false` while the help site still refuses
 * framing; the manual page then links out instead of rendering a broken frame.
 */
export const HELP_SITE_EMBED: boolean = import.meta.env.VITE_HELP_EMBED !== 'false'

// Central place to store permission slugs used in the frontend
export enum Permission {
  // User / organization users
  CREATE_OWN_CUSTOMER_USER = 'create_own_customer_user',
  READ_OWN_CUSTOMER_USER = 'read_own_customer_user',
  UPDATE_OWN_CUSTOMER_USER = 'update_own_customer_user',
  DELETE_OWN_CUSTOMER_USER = 'delete_own_customer_user',

  // Categories / sub-categories
  READ_CATEGORY = 'read_category',
  CREATE_SUB_CATEGORY = 'create_sub_category',
  READ_SUB_CATEGORY = 'read_sub_category',
  UPDATE_SUB_CATEGORY = 'update_sub_category',
  DELETE_SUB_CATEGORY = 'delete_sub_category',

  // Items / sets
  CREATE_SET = 'create_set',
  READ_SET = 'read_set',
  UPDATE_SET = 'update_set',
  DELETE_SET = 'delete_set',
  READ_ITEM = 'read_item',

  // Planning
  CREATE_PLANNING = 'create_planning',
  READ_PLANNING = 'read_planning',
  UPDATE_PLANNING = 'update_planning',
  DELETE_PLANNING = 'delete_planning',

  // Tags
  CREATE_TAG = 'create_tag',
  READ_TAG = 'read_tag',
  UPDATE_TAG = 'update_tag',
  DELETE_TAG = 'delete_tag',
}

export enum PlanStatus {
  // The backend's initial state is `created`; `draft` is kept for backwards
  // compatibility with older records. Both are treated as the editable,
  // not-yet-ordered ("draft") state throughout the UI.
  CREATED = 'created',
  DRAFT = 'draft',
  ORDERED = 'ordered',
}

/**
 * Whether a plan is still in its editable, not-yet-ordered state. Covers both
 * the current backend value (`created`) and the legacy `draft` value.
 */
export function isDraftStatus(status: string | undefined | null): boolean {
  return status === PlanStatus.CREATED || status === PlanStatus.DRAFT
}

/**
 * Whether the linked Work4All project's own status still reads as a draft.
 *
 * `status` above cannot answer this on its own: the API maps every Work4All
 * status code outside the setup and shipping trees to `created`, so a project a
 * Work4All user parked in "02 In Bearbeitung" or "00 Anfrage" arrives here
 * looking exactly like a real draft. The raw status name does still tell them
 * apart — a draft is "41 A-Erstellt" (setup) or "21 V-Erstellt" (shipping), and
 * every other status in both trees ends in a different word.
 *
 * Matched on the name rather than the code because the codes differ between the
 * PROD and QA tenants, so no code list would be correct on both.
 *
 * A missing project or name returns `true`, leaving the decision to `status`
 * alone — the same answer this code gave before the check existed. The backend
 * guards the operation regardless; this only keeps the UI from offering an
 * action that would come back refused.
 */
export function projectStatusLooksLikeDraft(
  project: { project_status?: { name?: string | null } | null } | null | undefined,
): boolean {
  const name = project?.project_status?.name?.trim()
  if (!name) return true
  return /erstellt$/i.test(name)
}

/**
 * Page sizes offered wherever a list is paginated, and the size a list starts
 * on.
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50]
export const DEFAULT_PAGE_SIZE = 10
